<template>
  <div>
    <contact-base-form :show-national-id='showNationalId' :show-birthdate='showBirthdate' :show-customer-ref='showCustomerRef' :with-contact-type='withContactType' :value='value.contactBase'></contact-base-form>
    <fieldset class='lib__form__fieldset'>
      <legend class='lib__form__legend'>{{ $t('common.contact.contactAddress') }}</legend>
      <template v-if='!contactAddressRequired'>
        <RadioButton v-model='useOtherAddress' :item='0' group='useOtherAddress'>
          <span>
            {{ $t('common.contact.complaintAddress') }} :
            <p class='lib__text-gray-500 lib__no-margin' v-html='address' />
          </span>
        </RadioButton>
        <RadioButton v-model='useOtherAddress' :item='1' group='useOtherAddress'>
          {{ $t('common.contact.otherAddress') }}
        </RadioButton>
      </template>
      <address-form v-if='useOtherAddress' v-model='value.otherAddress'></address-form>
      <hr />

      <div v-if='!hideOtherContact'>
        <div class='contact-person-mobile'>
          <p class='lib__form__legend contact-person-mobile header'>{{ $t('common.contact.optionalPerson') }}</p>
          <p class='lib__text-gray-600 contact-person-mobile text-info'>{{ $t('common.contact.addPersonTips') }}</p>
          <div v-if='addOtherContact' id='contactsContainer'>
            <contact-person-form :showBirthdate='false' :value='value.otherContact'
                                 @remove='addOtherContact = false'></contact-person-form>
          </div>
          <div v-if='!addOtherContact' class='contact-person-mobile button-container'>
            <button class='lib__btn lib__btn--secondary lib__btn--rounded contact-person-mobile button'
                    type='button' @click.prevent='addOtherContact = true'>
              <i class='fas fa-plus'></i>
              <span>{{ $t('common.contact.addPerson') }}</span>
            </button>
          </div>
        </div>
        <div class='contact-person-desktop'>
          <contact-person-form v-if='addOtherContact' :showBirthdate='false' :value='value.otherContact'
                               @remove='addOtherContact = false'></contact-person-form>
          <button v-if='!addOtherContact' class='lib__btn-action' type='button' @click.prevent='addOtherContact = true'>
            <span class='lib__badge lib__badge--primary'>
              <i class='fas fa-plus'></i>
            </span>
            <span>
              <span class='lib__btn-action__label'>{{ $t('common.contact.addPerson') }}</span>
              <small v-html="$t('common.contact.addPersonTips')"></small>
            </span>
          </button>
        </div>
      </div>
    </fieldset>
  </div>
</template>

<script lang='ts'>
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { contactService } from '@/services'
import { AddressPoint, CategorySelectionStepModel, ConsumptionAddress, ContactInfo } from '@/models'
import ContactBaseForm from '@/components/inputs/contact/ContactBaseForm.vue'
import AddressForm from '@/components/inputs/contact/AddressForm.vue'
import ContactPersonForm from '@/components/inputs/contact/ContactPersonForm.vue'
import { State } from 'vuex-class'

@Component({
  name: 'contact-info-form',
  components: {
    ContactBaseForm,
    ContactPersonForm,
    AddressForm,
  },
})
export default class ContactForm extends Vue {
  //#region [Property]
  @Prop({ type: Object, required: true }) public readonly value!: ContactInfo
  @Prop({ type: Object, required: false, default: null }) public addressPoint!: AddressPoint | null
  @Prop({ type: Object, required: false, default: null }) public consumptionAddress!: ConsumptionAddress | null
  @Prop({ type: Boolean, required: false, default: false }) public contactAddressRequired!: boolean
  @Prop({ type: Boolean, required: false, default: false }) public hideOtherContact!: boolean
  @Prop({ type: Boolean, required: false, default: true }) public readonly showBirthdate!: boolean
  @Prop({ type: Boolean, required: false, default: false }) public readonly showNationalId!: boolean
  @Prop({ type: Boolean, required: false, default: false }) public readonly showCustomerRef!: boolean
  @Prop({ type: Boolean, required: false, default: true }) public readonly withContactType!: boolean
  //#endregion

  //#region [Data]
  @State('category', { namespace: 'complaint/categorySelection' })
  public categorySelectionState!: CategorySelectionStepModel | null
  //#endregion

  public get useOtherAddress(): number {
    return this.value.useOtherAddress ? 1 : 0
  }

  public set useOtherAddress(value: number) {
    this.value.useOtherAddress = !!value
  }

  public get addOtherContact(): boolean {
    return !!this.value.addOtherContact
  }

  public set addOtherContact(value: boolean) {
    this.value.addOtherContact = value
  }

  //#region [Computed]
  private get address() {
    let str = ''
    if (this.addressPoint) {
      if (this.addressPoint.streetSubCity) {
        if (this.addressPoint.houseNumber) {
          str += this.addressPoint.houseNumber
        }
        if (this.addressPoint.houseBox) {
          str += ' / ' + this.addressPoint.houseBox
        }
        str += ' ' + this.addressPoint.streetSubCity.name + '<br>'
      }
      str += this.addressPoint.subCity.zipCode
      str += ' ' + this.addressPoint.subCity.cityName
      return str
    }
    if (this.consumptionAddress) {
      str += this.consumptionAddress.houseNumber
      str += ' ' + this.consumptionAddress.streetName
      if (this.consumptionAddress.complement) {
        str += ' ' + this.consumptionAddress.complement
      }
      if (this.consumptionAddress.complementBox) {
        str += ' ' + this.consumptionAddress.complementBox
      }
      if (this.consumptionAddress.floorNumber) {
        str += ` (${this.$t(`common.identification.floor`).toString()}: ${this.consumptionAddress.floorNumber})`
      }
      str += '<br>' + this.consumptionAddress.zipCode
      str += ' ' + this.consumptionAddress.cityName
      return str
    }
    return ''
  }

  //#endregion

  //#region [Watch]
  @Watch('value.useOtherAddress')
  public onUseOtherAddressChanged(value: boolean) {
    if (value && this.value.otherAddress === null) {
      this.value.otherAddress = contactService.createContactAddress()
    } else if (!value && this.value.otherAddress !== null) {
      this.value.otherAddress = null
    }
  }

  @Watch('value.addOtherContact')
  public onAddOtherContactChanged(value: boolean) {
    if (value && this.value.otherContact === null) {
      this.value.otherContact = contactService.createContactPerson()
    } else if (!value && this.value.otherContact !== null) {
      this.value.otherContact = null
    }
  }

  //#endregion
}
</script>

<style scoped>
@media screen and (max-width: 576px) {
  .lib__btn.contact-person-mobile.button {
    text-transform: none;
    width: 320px;
  }

  div.contact-person-mobile.button-container {
    width: 320px;
    margin: 20px auto;
  }

  div.contact-person-mobile {
    display: block;
    width: 100%
  }

  div.contact-person-desktop {
    display: none;
  }

  p.contact-person-mobile.header {
    margin-bottom: 20px;
  }

  p.lib__text-gray-600.contact-person-mobile.text-info {
    font-size: 12px;

  }
}

@media screen and (min-width: 577px) {
  div.contact-person-desktop {
    display: initial;
  }

  div.contact-person-mobile {
    display: none;
  }
}
</style>
